import { IAssessmentRating } from './interfaces';
import { YesNo } from '../../../../../components/radio-group/yes-no-component';
import {
  AssessmentStatusDto,
  FunctionalAbilityCatalogItem,
  SubThemeRatingDto,
  GuardianTypeDto,
  GuardianTypeModel,
  RepresentationTypeDto,
  RepresentationTypeModel,
} from '../../../../../api-service/clients';
import { IActivityAndParticipationSummary } from './activity-and-participation-rating-summary';

export function hexToRGB(hex: string) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return { r, g, b };
}

export const defaultRatings: IAssessmentRating[] = [
  { stepText: 'Ingen nedsat funktionsevne', name: '0', value: 0, helpText: 'Borgeren udfører alle eller de fleste relevante aktiviteter' },
  { stepText: 'Let nedsat funktionsevne', name: '1', value: 1, helpText: 'Borgeren udfører for det meste alle eller de fleste relevante aktiviteter' },
  { stepText: 'Moderat nedsat funktionsevne', name: '2', value: 2, helpText: 'Borgeren udfører i perioder de fleste eller nogle relevante aktiviteter' },
  { stepText: 'Svært nedsat funktionsevne', name: '3', value: 3, helpText: 'Borgeren udfører en enkelt gang imellem nogle eller få relevante aktiviteter' },
  { stepText: 'Helt nedsat funktionsevne', name: '4', value: 4, helpText: 'Borgeren udfører sjældent eller aldrig relevante aktiviteter' },
];

export function getYesNoFromBoolean(value?: boolean): YesNo {
  switch (value) {
    case true:
      return YesNo.Yes;
    case false:
      return YesNo.No;
    default:
      return YesNo.None;
  }
}

export function getBooleanFromYesNo(value: YesNo): boolean | undefined {
  switch (value) {
    case YesNo.Yes:
      return true;
    case YesNo.No:
      return false;
    case YesNo.None:
      return undefined;
    default:
      return undefined;
  }
}

// Temp data - this should come from codelists
export interface IIssueTheme {
  id: string;
  name: string;
}

export interface ISubThemeRating {
  subThemeId: string;
  name: string;
  rating?: number;
  citizenComment?: string;
  caseWorkerComment?: string;
}

export function translateAssessmentStatus(status: AssessmentStatusDto) {
  switch (status) {
    case AssessmentStatusDto.UnderPreparation:
      return 'Under udarbejdelse';
    case AssessmentStatusDto.Completed:
      return 'Færdiggjort';
    case AssessmentStatusDto.Draft:
      return 'Kladde';
    case AssessmentStatusDto.Current:
      return 'Aktuel';
    case AssessmentStatusDto.Historic:
      return 'Historisk';
    default:
      return '';
  }
}

export function getAssessmentStatusColor(status: AssessmentStatusDto): 'default' | 'success' | 'secondary' | 'warning' {
  switch (status) {
    case AssessmentStatusDto.Draft:
      return 'warning';
    case AssessmentStatusDto.Current:
      return 'success';
    case AssessmentStatusDto.Historic:
      return 'secondary';
    default:
      return 'default';
  }
}

export interface AreaAnswer {
  area: string;
  mainQuestion: boolean | undefined;
  secondaryQuestion: boolean | undefined;
}

export function getNoAnswerSubThemes(
  state: IActivityAndParticipationSummary,
  functionalAbilityCatalogItems: FunctionalAbilityCatalogItem[],
  secondaryQuestion: boolean | undefined
): SubThemeRatingDto[] {
  const { relations, societalLife, communication, practicalAssignments, selfCare, mobility } = state;

  const areaAnswers: AreaAnswer[] = [
    { area: 'Relationer', mainQuestion: relations?.mainQuestion, secondaryQuestion: relations?.secondaryQuestion },
    { area: 'Samfundsliv', mainQuestion: societalLife?.mainQuestion, secondaryQuestion: societalLife?.secondaryQuestion },
    { area: 'Kommunikation', mainQuestion: communication?.mainQuestion, secondaryQuestion: communication?.secondaryQuestion ?? undefined },
    { area: 'Praktiske opgaver', mainQuestion: practicalAssignments?.mainQuestion, secondaryQuestion: practicalAssignments?.secondaryQuestion ?? undefined },
    { area: 'Egenomsorg', mainQuestion: selfCare?.mainQuestion, secondaryQuestion: selfCare?.secondaryQuestion ?? undefined },
    { area: 'Mobilitet', mainQuestion: mobility?.mainQuestion, secondaryQuestion: mobility?.secondaryQuestion ?? undefined },
  ];

  const noAnswerSubThemes: SubThemeRatingDto[] = [];

  for (const answer of areaAnswers) {
    if (answer.mainQuestion === false && answer.secondaryQuestion === secondaryQuestion) {
      const subThemes = functionalAbilityCatalogItems
        .filter(item => item.mainThemeDescription === answer.area)
        .map(item => ({ functionalEventCatalogId: item.id } as SubThemeRatingDto));
      noAnswerSubThemes.push(...subThemes);
    }
  }

  return noAnswerSubThemes;
}

export type GuardianTypeValue = 'guardianship' | 'guardianshipWithRevokedLegalCapacity' | 'jointGuardianship' | 'none';

export function toGuardianTypeDto(value?: GuardianTypeValue): GuardianTypeDto | undefined {
  if (!value || value === 'none') return undefined;
  return value as GuardianTypeDto;
}

export function fromGuardianTypeDto(value?: GuardianTypeDto): GuardianTypeValue | undefined {
  if (!value) return undefined;
  return value as GuardianTypeValue;
}

export function toGuardianTypeModel(value?: GuardianTypeValue): GuardianTypeModel | undefined {
  if (!value || value === 'none') return undefined;
  return value as GuardianTypeModel;
}

export function fromGuardianTypeModel(value?: GuardianTypeModel): GuardianTypeValue | undefined {
  if (!value) return undefined;
  return value as GuardianTypeValue;
}

export type RepresentationTypeValue = 'accompanyingPerson' | 'authorizedRepresentative' | 'none';

export function toRepresentationTypeDto(value?: RepresentationTypeValue): RepresentationTypeDto | undefined {
  if (!value || value === 'none') return undefined;
  return value as RepresentationTypeDto;
}

export function fromRepresentationTypeDto(value?: RepresentationTypeDto): RepresentationTypeValue | undefined {
  if (!value) return undefined;
  return value as RepresentationTypeValue;
}

export function toRepresentationTypeModel(value?: RepresentationTypeValue): RepresentationTypeModel | undefined {
  if (!value || value === 'none') return undefined;
  return value as RepresentationTypeModel;
}

export function fromRepresentationTypeModel(value?: RepresentationTypeModel): RepresentationTypeValue | undefined {
  if (!value) return undefined;
  return value as RepresentationTypeValue;
}

export function upsertSubThemeRatings(selected: FunctionalAbilityCatalogItem[], existing?: SubThemeRatingDto[]): SubThemeRatingDto[] {
  return selected.map(option => existing?.find(rating => rating.functionalEventCatalogId === option.id) ?? ({ functionalEventCatalogId: option.id } as SubThemeRatingDto));
}
